import { whenFirstPaintDone } from './firstPaintSignal'

/**
 * Run speculative work once the hot path is done AND the browser is idle.
 *
 * Used for work that warms a path the member may never take — the chat socket,
 * the Chat chunk, background polls. Doing any of it at mount puts it in direct
 * competition with the requests the current page actually needs (IN-46 Phase 4).
 *
 * The ORDER matters and idle alone is not enough. A cold start is network-bound:
 * the main thread is idle for the whole of `/auth/refresh` and again while the
 * dashboard's queries are in flight, so `requestIdleCallback` fires straight into
 * the window this is meant to protect. Measured — deferring the Chat chunk with
 * idle alone still had it loading before the dashboard cards appeared. So: wait
 * for the application signal first, then idle within it.
 *
 * `requestIdleCallback` is unavailable in Safari before 17 and in jsdom, so the
 * fallback is a plain timeout. The timeout is deliberately long: a short one on a
 * slow phone lands in the middle of exactly the burst this is avoiding.
 *
 * Returns a cancel function, so an effect that unmounts before the callback fires
 * does not run it afterwards.
 */
const IDLE_FALLBACK_MS = 3000

export function scheduleWhenIdle(fn: () => void, timeoutMs = IDLE_FALLBACK_MS): () => void {
  let cancelInner: () => void = () => {}
  const cancelOuter = whenFirstPaintDone(() => {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(() => fn(), { timeout: timeoutMs })
      cancelInner = () => window.cancelIdleCallback?.(id)
      return
    }
    const id = setTimeout(fn, timeoutMs)
    cancelInner = () => clearTimeout(id)
  })
  return () => {
    cancelOuter()
    cancelInner()
  }
}
